import { DeleteOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { rolesConfig } from 'config/rolesConfig';
import { formatDisplayName } from 'library/helpers/utility';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { roleActions } from 'reduxStore/roles/slice';
import { Button, Divider, Flex, Modal, Space, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const DeleteGlobalAdminModal = ({ children, personIDs = [], open, setOpen }) => {
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();

	const dispatch = useDispatch();
	const [internalVisible, setInternalVisible] = useState(false);
	const visible = open !== undefined ? open : internalVisible;
	const setVisible = setOpen !== undefined ? setOpen : setInternalVisible;

	const people = useSelector(selectPeople);
	const { loadingRevokeRole: loading } = useSelector((state) => state.Roles);
	const { orgID } = useSelector((state) => state.Auth.selectedProfile);

	const filteredPeople = useMemo(() => people.filter((p) => personIDs.includes(p.personID)), [people, personIDs]);

	const selectedNames = useMemo(
		() => filteredPeople.map((person) => formatDisplayName(person)?.displayName || `#${person.personID}`),
		[filteredPeople]
	);

	const generateDeleteTitle = () =>
		personIDs.length > 1
			? `Are you sure you want to revoke these ${personIDs.length} Global Admins?`
			: 'Are you sure you want to revoke this admin? ';

	const generateDeleteButtonText = () => (personIDs.length > 1 ? 'Revoke Global Admins' : 'Revoke Admin');

	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	const handleDelete = () => {
		for (const personID of personIDs) {
			dispatch(
				roleActions.revokeRole({
					personID,
					permissionID: rolesConfig.GLOBAL_ADMIN,
					entityID: orgID
				})
			);
		}

		handleCancel();
	};

	return (
		<Fragment>
			<Tooltip title='Remove Global Admin' placement='top' color='red'>
				{children ? (
					children
				) : (
					<Button
						icon={<DeleteOutlined />}
						onClick={() => setVisible(true)}
						disabled={loading}
						danger
						type='primary'
					>
						{generateDeleteButtonText()}
					</Button>
				)}
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
						gap={12}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button
							onClick={handleDelete}
							loading={loading}
							disabled={loading}
							type='primary'
							danger
							block
						>
							{generateDeleteButtonText()}
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: paddingXL } }}
			>
				<Flex vertical align='center' justify='center' gap={paddingSM}>
					<DeletedFilled />
					<Flex vertical gap={paddingLG}>
						<Space
							size={paddingXS}
							direction='vertical'
							align='center'
							style={{ margin: `${paddingXS}px 0px` }}
						>
							<Title
								level={5}
								style={{
									margin: 0,
									lineHeight: '24px',
									textAlign: 'center',
									padding: `0px ${paddingXL}px`
								}}
							>
								{generateDeleteTitle()}
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									lineHeight: '24px',
									textAlign: 'center',
									display: 'block'
								}}
							>
								This action is permanent and <br /> cannot be undone.
							</Text>
						</Space>

						<Flex vertical gap={marginXS}>
							<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
								<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Admin</Text>
							</Divider>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
								{selectedNames.join(', ')}
							</Text>
						</Flex>
					</Flex>
				</Flex>
			</Modal>
		</Fragment>
	);
};

export default DeleteGlobalAdminModal;
